import {
  MortgageTrackType,
  MortgageType,
  Mortgage,
  MortgageTrack,
  MortgageTrackRepaymentType,
  MortgageTrackIndexationType,
  MortgageInterestBaseType,
  PropertyType,
  MortgageDetailsExtracted,
} from "../interfaces/DTO/mortgage/mortgage";

// ----- Formatters (Enum -> Hebrew Text) -----
export const formatMortgageTrackType = (type: MortgageTrackType): string => {
  switch (type) {
    case MortgageTrackType.FIXED_LINKED:
      return "קבועה צמודה";
    case MortgageTrackType.FIXED_UNLINKED:
      return "קבועה לכל התקופה";
    case MortgageTrackType.PRIME:
      return "משתנה פריים";
    case MortgageTrackType.VARIABLE_UNLINKED_18M:
      return "משתנה לא צמודה כל 18 חודשים על בסיס אג\"ח ממשלתי";
    case MortgageTrackType.VARIABLE_LINKED_3Y:
      return "משתנה צמודה כל 3 שנים על בסיס אג\"ח ממשלתי";
    case MortgageTrackType.VARIABLE_UNLINKED_3Y:
      return "משתנה לא צמודה כל 3 שנים על בסיס אג\"ח ממשלתי";
    case MortgageTrackType.VARIABLE_LINKED_5Y:
      return "משתנה צמודה כל 5 שנים על בסיס אג\"ח ממשלתי";
    case MortgageTrackType.VARIABLE_UNLINKED_5Y:
      return "משתנה לא צמודה כל 5 שנים על בסיס אג\"ח ממשלתי";
    case MortgageTrackType.VARIABLE_LINKED_10Y:
      return "משתנה צמודה כל 10 שנים על בסיס אג\"ח ממשלתי";
    case MortgageTrackType.VARIABLE_UNLINKED_10Y:
      return "משתנה לא צמודה כל 10 שנים על בסיס אג\"ח ממשלתי";
    default:
      return "";
  }
};

export const formatMortgageType = (type: MortgageType): string => {
  switch (type) {
    case MortgageType.UNIFORM_1:
      return "סל אחיד 1";
    case MortgageType.UNIFORM_2:
      return "סל אחיד 2";
    case MortgageType.UNIFORM_3:
      return "סל אחיד 3";
    case MortgageType.CUSTOM:
      return "מותאם אישית";
    default:
      return "";
  }
};

export const formatMortgageTrackRepaymentType = (type: MortgageTrackRepaymentType): string => {
  switch (type) {
    case MortgageTrackRepaymentType.SPITZER:
      return "שפיצר – קרן בהחזר עולה, ריבית בהחזר קטנה";
    case MortgageTrackRepaymentType.FIXED_PRINCIPAL:
      return "קרן קבועה – הקרן קבועה וההחזר יורד";
    case MortgageTrackRepaymentType.BULLET:
      return "בלון – החזר קרן בסוף וריבית שוטפת";
    default:
      return "";
  }
};

export const formatMortgageTrackIndexationType = (type: MortgageTrackIndexationType): string => {
  switch (type) {
    case MortgageTrackIndexationType.NONE:
      return "לא צמודה";
    case MortgageTrackIndexationType.CPI:
      return "צמוד למדד";
    case MortgageTrackIndexationType.PRIME:
      return "פריים";
    case MortgageTrackIndexationType.GOVERNMENT_BOND:
      return "אג\"ח ממשלתי";
    default:
      return "";
  }
};

export const formatMortgageInterestBaseType = (type: MortgageInterestBaseType): string => {
  switch (type) {
    case MortgageInterestBaseType.FIXED:
      return "קבועה";
    case MortgageInterestBaseType.PRIME:
      return "פריים";
    case MortgageInterestBaseType.GOV_BOND:
      return "אג\"ח ממשלתי";
    default:
      return "";
  }
};

export const formatPropertyType = (type: PropertyType): string => {
  switch (type) {
    case PropertyType.UNDER_CONSTRUCTION:
      return "בית בבניה";
    case PropertyType.FROM_CONTRACTOR:
      return "בית מקבלן";
    case PropertyType.READY_MADE:
      return "בית קיים";
    default:
      return "";
  }
};
/**
 * ריבית פריים נוכחית
 * Prime interest rate - current market rate
 */
export const CURRENT_PRIME_RATE = 5.75;

/**
 * תשואות אג"ח ממשלתי נוכחיות לפי תקופות
 * Government bond yields by duration
 */
export const GOVERNMENT_BOND_YIELDS = {
  "1_year": 4.15,
  "3_years": 3.78,
  "5_years": 3.75,
  "10_years": 3.955,
  "20_years": 4.217,
  "30_years": 4.497,
};


export const MortgageTrackTemplates: Record<MortgageTrackType, MortgageTrack> = {
  FIXED_LINKED: <MortgageTrack>{
    track_type: MortgageTrackType.FIXED_LINKED,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.CPI,
    interest_base_type: MortgageInterestBaseType.FIXED,
    months: 240,
    interest_rate: 4.84,
  },
  FIXED_UNLINKED: <MortgageTrack>{
    track_type: MortgageTrackType.FIXED_UNLINKED,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.NONE,
    interest_base_type: MortgageInterestBaseType.FIXED,
    months: 240,
    interest_rate: 4.84,
  },
  PRIME: {
    track_type: MortgageTrackType.PRIME,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.PRIME,
    interest_base_type: MortgageInterestBaseType.PRIME,
    months: 240,
    interest_rate: CURRENT_PRIME_RATE-0.8,
    interest_margin: -0.8,
    interest_update_interval_years:null,
  },
  VARIABLE_UNLINKED_18M: {
    track_type: MortgageTrackType.VARIABLE_UNLINKED_18M,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.NONE,
    interest_base_type: MortgageInterestBaseType.GOV_BOND,
    months: 240,
    interest_rate: GOVERNMENT_BOND_YIELDS["1_year"] + 1.5,
    interest_margin: 1.5,
    interest_update_interval_years: 1.5,
  },
  VARIABLE_LINKED_3Y: {
    track_type: MortgageTrackType.VARIABLE_LINKED_3Y,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.CPI,
    interest_base_type: MortgageInterestBaseType.GOV_BOND,
    months: 240,
    interest_rate: GOVERNMENT_BOND_YIELDS["3_years"] + 1.5,
    interest_margin: 1.5,
    interest_update_interval_years: 3,
  },
  VARIABLE_UNLINKED_3Y: {
    track_type: MortgageTrackType.VARIABLE_UNLINKED_3Y,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.NONE,
    interest_base_type: MortgageInterestBaseType.GOV_BOND,
    months: 240,
    interest_rate: GOVERNMENT_BOND_YIELDS["3_years"] + 1.5,
    interest_margin: 1.5,
    interest_update_interval_years: 3,
  },
  VARIABLE_LINKED_5Y: {
    track_type: MortgageTrackType.VARIABLE_LINKED_5Y,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.CPI,
    interest_base_type: MortgageInterestBaseType.GOV_BOND,
    months: 240,
    interest_rate: GOVERNMENT_BOND_YIELDS["5_years"] + 1.5,
    interest_margin: 1.5,
    interest_update_interval_years: 5,
  },
  VARIABLE_UNLINKED_5Y: {
    track_type: MortgageTrackType.VARIABLE_UNLINKED_5Y,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.NONE,
    interest_base_type: MortgageInterestBaseType.GOV_BOND,
    months: 240,
    interest_rate: GOVERNMENT_BOND_YIELDS["5_years"] + 1.5,
    interest_margin: 1.5,
    interest_update_interval_years: 5,
  },
  VARIABLE_LINKED_10Y: {
    track_type: MortgageTrackType.VARIABLE_LINKED_10Y,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.CPI,
    interest_base_type: MortgageInterestBaseType.GOV_BOND,
    months: 240,
    interest_rate: GOVERNMENT_BOND_YIELDS["10_years"] + 1.5,
    interest_margin: 1.5,
    interest_update_interval_years: 10,
  },
  VARIABLE_UNLINKED_10Y: {
    track_type: MortgageTrackType.VARIABLE_UNLINKED_10Y,
    repayment_type: MortgageTrackRepaymentType.SPITZER,
    indexation_type: MortgageTrackIndexationType.NONE,
    interest_base_type: MortgageInterestBaseType.GOV_BOND,
    months: 240,
    interest_rate: GOVERNMENT_BOND_YIELDS["10_years"] + 1.5,
    interest_margin: 1.5,
    interest_update_interval_years: 10,
  },
};

// ----- Ready-made Basket Templates -----

export const UNIFORM_BASKET_1: Mortgage = {
  basket_type: MortgageType.UNIFORM_1,
  tracks: [
    {
      ...MortgageTrackTemplates.FIXED_UNLINKED,
      amountPercentage: 1.0,
    },
  ],
};

export const UNIFORM_BASKET_2: Mortgage = {
  basket_type: MortgageType.UNIFORM_2,
  tracks: [
    {
      ...MortgageTrackTemplates.FIXED_UNLINKED,
      amountPercentage: 1 / 3,
    },
    {
      ...MortgageTrackTemplates.PRIME,
      amountPercentage: 1 / 3,
    },
    {
      ...MortgageTrackTemplates.VARIABLE_LINKED_5Y,
      amountPercentage: 1 / 3,
    },
  ],
};

export const UNIFORM_BASKET_3: Mortgage = {
  basket_type: MortgageType.UNIFORM_3,
  tracks: [
    {
      ...MortgageTrackTemplates.FIXED_UNLINKED,
      amountPercentage: 0.5,
    },
    {
      ...MortgageTrackTemplates.PRIME,
      amountPercentage: 0.5,
    },
  ],
};

export const UNIFORM_BASKETS: Partial<Record<MortgageType, Mortgage>> = {
  UNIFORM_1: UNIFORM_BASKET_1,
  UNIFORM_2: UNIFORM_BASKET_2,
  UNIFORM_3: UNIFORM_BASKET_3,
};

export const getMortgageTrackExplanation = (type: MortgageTrackType): string => {
  switch (type) {
    case MortgageTrackType.FIXED_UNLINKED:
      return "ריבית קבועה שאינה משתנה לאורך התקופה ואינה צמודה למדד. מתאימה למבקשים ודאות מלאה בגובה ההחזר.";
    case MortgageTrackType.FIXED_LINKED:
      return "ריבית קבועה לאורך כל התקופה, כשהקרן צמודה למדד המחירים לצרכן. החזר חודשי משתנה בהתאם לשינויים במדד.";
    case MortgageTrackType.PRIME:
      return "ריבית מבוססת פריים (ריבית בנק ישראל + מרווח קבוע). מתעדכנת אחת לחודש. הריבית עשויה להשתנות בהתאם לשוק.";
    case MortgageTrackType.VARIABLE_UNLINKED_18M:
      return "הריבית משתנה כל 18 חודשים לפי תשואות אג\"ח ממשלתית, אך הקרן אינה צמודה. שילוב של גמישות ויציבות יחסית.";
    case MortgageTrackType.VARIABLE_LINKED_3Y:
    case MortgageTrackType.VARIABLE_LINKED_5Y:
    case MortgageTrackType.VARIABLE_LINKED_10Y:
      return "ריבית משתנה כל מספר שנים על בסיס אג\"ח ממשלתית צמודה למדד. הקרן גם כן צמודה. מתאים למי שמאמין ביציבות המדד.";
    case MortgageTrackType.VARIABLE_UNLINKED_3Y:
    case MortgageTrackType.VARIABLE_UNLINKED_5Y:
    case MortgageTrackType.VARIABLE_UNLINKED_10Y:
      return "ריבית משתנה כל מספר שנים לפי אג\"ח ממשלתי לא צמוד. הקרן אינה צמודה. מתאים למי שמעדיף מינימום תנודתיות בקרן.";
    default:
      return "מסלול משכנתא מותאם אישית.";
  }
};

// ----- Market Rates (Current as of December 2024) -----

/**
 * חישוב סכום מקורי כולל מסיכום המסלולים
 * Calculate total original amount from tracks sum
 */
export const calculateTotalOriginalAmount = (tracks?: MortgageTrack[]): number => {
  if (!tracks || tracks.length === 0) return 0;

  return tracks.reduce((total, track) => {
    // מעדיף original_amount, אם לא קיים משתמש ב-amount
    const trackAmount = track.original_amount ?? track.amount ?? 0;
    return total + trackAmount;
  }, 0);
};

/**
 * חישוב יתרה כוללת נוכחית מסיכום המסלולים
 * Calculate total current balance from tracks sum
 */
export const calculateTotalCurrentBalance = (tracks?: MortgageTrack[]): number => {
  if (!tracks || tracks.length === 0) return 0;

  return tracks.reduce((total, track) => {
    // מעדיף current_balance, אם לא קיים משתמש ב-amount או original_amount
    const trackBalance = track.current_balance ?? track.amount ?? track.original_amount ?? 0;
    return total + trackBalance;
  }, 0);
};

/**
 * חישוב תשלום חודשי כולל מסיכום המסלולים
 * Calculate total monthly payment from tracks sum
 */
export const calculateTotalMonthlyPayment = (tracks?: MortgageTrack[]): number => {
  if (!tracks || tracks.length === 0) return 0;

  return tracks.reduce((total, track) => {
    const trackPayment = track.monthly_payment ?? 0;
    return total + trackPayment;
  }, 0);
};

/**
 * השלמת נתונים חסרים במשכנתא שחולצה
 * אם total_original_amount או total_current_balance או monthly_payment_total חסרים, מחשב אותם מסיכום המסלולים
 * Enrich extracted mortgage data by calculating missing totals from tracks
 */
export const enrichMortgageDetails = (mortgageDetails: MortgageDetailsExtracted): MortgageDetailsExtracted => {
  const enriched = { ...mortgageDetails };

  // אם אין סכום מקורי כולל, חשב מהמסלולים
  if (!enriched.total_original_amount && enriched.tracks && enriched.tracks.length > 0) {
    enriched.total_original_amount = calculateTotalOriginalAmount(enriched.tracks);
  }

  // אם אין יתרה כוללת נוכחית, חשב מהמסלולים
  if (!enriched.total_current_balance && enriched.tracks && enriched.tracks.length > 0) {
    enriched.total_current_balance = calculateTotalCurrentBalance(enriched.tracks);
  }

  // אם אין תשלום חודשי כולל, חשב מהמסלולים
  if (!enriched.monthly_payment_total && enriched.tracks && enriched.tracks.length > 0) {
    const calculated = calculateTotalMonthlyPayment(enriched.tracks);
    if (calculated > 0) {
      enriched.monthly_payment_total = calculated;
    }
  }

  return enriched;
};

